import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import {
  Save,
  Business,
  Language,
  Storage,
  RestartAlt,
} from "@mui/icons-material";
import {
  fetchSystemSettings,
  saveSystemSettings,
  clearAdminError,
  clearAdminSuccess,
} from "../../redux/slices/adminSlice";
import { SPACING } from "../../theme/constants";

const AdminSettings = () => {
  const dispatch = useDispatch();
  const { settings, settingsLoading, error, successMessage } = useSelector(
    (state) => state.admin,
  );
  const [localSettings, setLocalSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    dispatch(fetchSystemSettings());
  }, [dispatch]);

  useEffect(() => {
    setLocalSettings(settings || {});
  }, [settings]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => dispatch(clearAdminSuccess()), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => dispatch(clearAdminError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleChange = (key, value) => {
    setLocalSettings({ ...localSettings, [key]: value });
    setHasChanges(true);
  };

  const handleSave = () => {
    dispatch(saveSystemSettings(localSettings));
    setHasChanges(false);
  };

  const handleReset = () => {
    setLocalSettings(settings || {});
    setHasChanges(false);
  };

  if (settingsLoading && Object.keys(settings).length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const settingsGroups = [
    {
      group: "General",
      icon: <Business fontSize="small" />,
      description: "Basic platform identity and contact information",
      fields: [
        {
          key: "site_name",
          label: "Platform Name",
          type: "text",
          placeholder: "WP Spark",
        },
        {
          key: "admin_email",
          label: "Admin Email",
          type: "email",
          placeholder: "admin@example.com",
        },
        {
          key: "support_email",
          label: "Support Email",
          type: "email",
          placeholder: "support@example.com",
        },
      ],
    },
    {
      group: "Site Defaults",
      icon: <Language fontSize="small" />,
      description: "Default configuration applied to newly created sites",
      fields: [
        {
          key: "default_php_version",
          label: "Default PHP Version",
          type: "text",
          placeholder: "8.2",
        },
        {
          key: "default_wp_version",
          label: "Default WordPress Version",
          type: "text",
          placeholder: "6.4",
        },
        {
          key: "max_sites_per_user",
          label: "Max Sites Per User",
          type: "number",
          placeholder: "5",
        },
        {
          key: "max_storage_per_site_mb",
          label: "Max Storage Per Site (MB)",
          type: "number",
          placeholder: "1024",
        },
      ],
    },
    {
      group: "Backups",
      icon: <Storage fontSize="small" />,
      description: "Backup retention and limits for all sites",
      fields: [
        {
          key: "backup_retention_days",
          label: "Backup Retention (Days)",
          type: "number",
          placeholder: "30",
        },
        {
          key: "max_backups_per_site",
          label: "Max Backups Per Site",
          type: "number",
          placeholder: "10",
        },
      ],
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: SPACING.lg,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: SPACING.xs }}>
            System Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure platform-wide defaults and limits
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {hasChanges && (
            <Button
              variant="outlined"
              startIcon={<RestartAlt />}
              onClick={handleReset}
              size="small"
            >
              Discard
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={!hasChanges}
          >
            Save Settings
          </Button>
        </Box>
      </Box>

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearAdminSuccess())}
        >
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearAdminError())}
        >
          {error}
        </Alert>
      )}

      {hasChanges && (
        <Alert severity="info" sx={{ mb: 2 }} icon={false}>
          You have unsaved changes.
        </Alert>
      )}

      {settingsGroups.map((group) => (
        <Card key={group.group} sx={{ mb: 2.5 }}>
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  bgcolor: "primary.light",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "primary.main",
                }}
              >
                {group.icon}
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {group.group}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {group.description}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              {group.fields.map((field) => (
                <Grid size={{ xs: 12, sm: 6 }} key={field.key}>
                  <TextField
                    fullWidth
                    label={field.label}
                    type={field.type}
                    placeholder={field.placeholder}
                    value={localSettings[field.key] || ""}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default AdminSettings;
